import i18n, { InitOptions } from 'i18next'
import { initReactI18next } from 'react-i18next'
import { bundledResources } from 'ClientPortal/src/i18n/resources'
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, SupportedLocale, TRANSLATION_NAMESPACES } from 'ClientPortal/src/i18n/supportedLocales'
import { writePersistedLocale } from 'ClientPortal/src/i18n/persistedLocale'

type InitializationResult = {
    didFallbackToDefaultLocale: boolean
}

let initializationPromise: Promise<InitializationResult> | undefined
let hasBootstrapped = i18n.isInitialized

function getInitOptions(locale: SupportedLocale): InitOptions {
    return {
        fallbackLng: DEFAULT_LOCALE,
        interpolation: {
            escapeValue: false,
        },
        load: 'currentOnly',
        lng: locale,
        ns: [...TRANSLATION_NAMESPACES],
        react: {
            useSuspense: true,
        },
        resources: bundledResources,
        supportedLngs: [...SUPPORTED_LOCALES],
    }
}

async function initializeWithLocale(locale: SupportedLocale) {
    await i18n.use(initReactI18next).init(getInitOptions(locale))
    writePersistedLocale(locale)
}

async function bootstrapI18n(locale: SupportedLocale): Promise<InitializationResult> {
    try {
        await initializeWithLocale(locale)
        return { didFallbackToDefaultLocale: false }
    } catch {
        await initializeWithLocale(DEFAULT_LOCALE)
        return { didFallbackToDefaultLocale: true }
    }
}

export async function initI18n(locale: SupportedLocale) {
    if (!hasBootstrapped) {
        if (!initializationPromise) {
            initializationPromise = bootstrapI18n(locale)
                .then((result) => {
                    hasBootstrapped = true
                    return result
                })
                .finally(() => {
                    initializationPromise = undefined
                })
        }

        const { didFallbackToDefaultLocale } = await initializationPromise

        if (didFallbackToDefaultLocale) {
            return i18n
        }
    }

    if (i18n.resolvedLanguage !== locale) {
        await i18n.changeLanguage(locale)
        writePersistedLocale(locale)
    }

    return i18n
}

export { i18n }
